/**
 * Register point-of-interest picker in the widget editor: what happens when the register schema
 * (GET api/register-data/{id}/schema) cannot be read.
 *
 * Since AVA-602 security (API a4929ece) the schema needs Registers.View plus owner access to the
 * register, so a dashboard author can be refused (403). The picker then has no link declarations
 * and hides; this note says why instead of leaving the option silently missing. Fixed copy only;
 * the server's text is never shown.
 *
 * Saving without the schema: a widget whose highlight/context is "inherit" (register default) needs
 * the register's DEFAULT link columns in its saved columns. Those are only knowable from the schema,
 * so without it the save keeps the columns the widget was saved with instead of recomputing them
 * (resolvePoiForcedColumns).
 *
 * No imports: unit-tested with `node --test`.
 */

export const POI_SCHEMA_DENIED_NOTE = "Point-of-interest highlighting can't be changed here: "
  + "you don't have access to this register's link settings.";

const HTTP_FORBIDDEN = 403;

/**
 * State of the register schema read behind the POI picker. Only OK means the declarations are real;
 * every other state leaves them empty.
 *   IDLE    - nothing requested (no register view resolved yet, or not a register view)
 *   PENDING - request in flight
 *   OK      - declarations loaded
 *   DENIED  - refused (403): the note shows
 *   ERROR   - any other failure (logged as an error, no note)
 */
export const POI_SCHEMA_STATUS = Object.freeze({
  IDLE: 'idle',
  PENDING: 'pending',
  OK: 'ok',
  DENIED: 'denied',
  ERROR: 'error',
});

/**
 * @param {unknown} err axios error from `registerService.getRegisterSchema`
 * @returns {boolean} true when the schema read was refused for lack of access
 */
export function isPoiSchemaAccessDenied(err) {
  return !!err && typeof err === 'object' && err.response?.status === HTTP_FORBIDDEN;
}

/**
 * @param {unknown} err a failed schema read
 * @returns {string} POI_SCHEMA_STATUS.DENIED for a 403, POI_SCHEMA_STATUS.ERROR otherwise
 */
export function poiSchemaStatusFromError(err) {
  return isPoiSchemaAccessDenied(err) ? POI_SCHEMA_STATUS.DENIED : POI_SCHEMA_STATUS.ERROR;
}

const isColumnName = value => typeof value === 'string' && value.trim() !== '';

/**
 * The register-default POI columns a widget save must add to its saved columns.
 *
 * - Schema read OK: recompute - the effective highlight/context columns resolved from the register's
 *   declarations (`effectiveColumns`, from computeEffectivePoi). The previous save is ignored.
 * - Schema NOT read (refused, failed, or still loading): the default columns cannot be known, so
 *   nothing is recomputed. When the saved widget inherits a register default (`inheritsDefault`),
 *   every column it was previously saved with that this save would otherwise drop is kept, so the
 *   default column the highlight reads is never lost. A column the view no longer has (not in
 *   `availableColumns`, once that list is loaded) is not carried - it would break the query. A new
 *   widget has no previous save (`previousColumns` null) and gets none.
 * - An EXPLICITLY chosen POI column is not handled here: the caller forces it by name regardless.
 *
 * PURE: returns a new array, never mutates its inputs.
 *
 * @param {object} args
 * @param {string} args.schemaStatus one of POI_SCHEMA_STATUS
 * @param {Array<?string>} [args.effectiveColumns] recomputed highlight/context columns (used on OK)
 * @param {boolean} args.inheritsDefault the save persists an EFFECTIVE register-default POI: Link 3D
 *   on, the legacy POI keys serialised, and the highlight is 'inherit' (or an explicit column over an
 *   'inherit' context). The caller decides; this module has no POI grammar.
 * @param {?Array<string>} args.previousColumns the columns the widget was last saved with; null for
 *   a widget that has never been saved
 * @param {Array<string>} args.currentColumns the columns this save already carries
 * @param {Array<string>} [args.availableColumns] the view's current column names; empty/absent (not
 *   loaded, or failed) means unknown and nothing is filtered
 * @returns {string[]} columns to add, in order, without duplicates or ones already carried
 */
export function resolvePoiForcedColumns({
  schemaStatus,
  effectiveColumns,
  inheritsDefault,
  previousColumns,
  currentColumns,
  availableColumns,
}) {
  const carried = new Set(Array.isArray(currentColumns) ? currentColumns : []);
  let candidates = [];
  if (schemaStatus === POI_SCHEMA_STATUS.OK) {
    candidates = Array.isArray(effectiveColumns) ? effectiveColumns : [];
  } else if (inheritsDefault && Array.isArray(previousColumns)) {
    const known = Array.isArray(availableColumns) && availableColumns.length
      ? new Set(availableColumns)
      : null;
    candidates = known ? previousColumns.filter(column => known.has(column)) : previousColumns;
  }
  return candidates.reduce((added, column) => {
    if (isColumnName(column) && !carried.has(column) && !added.includes(column)) {
      return [...added, column];
    }
    return added;
  }, []);
}
